/**
 * Image Optimization Utility
 *
 * Provides helper functions for optimizing images from various sources
 * including Wikimedia, Unsplash, and custom images.
 *
 * Features:
 * - Automatic WebP conversion
 * - Responsive image sizes
 * - CDN-based optimization
 * - Lazy loading support
 */

/**
 * Optimize image URL using images.weserv.nl CDN
 *
 * @param {string} imageUrl - Original image URL
 * @param {object} options - Optimization options
 * @param {number} options.width - Target width
 * @param {number} options.height - Target height (optional)
 * @param {number} options.quality - Quality 1-100 (default: 85)
 * @param {string} options.format - Output format: 'webp', 'jpg', 'png' (default: 'webp')
 * @param {string} options.fit - Fit mode: 'cover', 'contain', 'fill' (default: 'cover')
 * @returns {string} Optimized image URL
 */
export const optimizeImage = (imageUrl, options = {}) => {
  const {
    width = 800,
    height = null,
    quality = 85,
    format = 'webp',
    fit = 'cover'
  } = options;

  // Remove protocol from URL
  const cleanUrl = imageUrl.replace(/^https?:\/\//, '');

  // Build optimization parameters
  const params = new URLSearchParams({
    url: cleanUrl,
    w: width,
    output: format,
    q: quality,
    fit: fit
  });

  // Add height if specified
  if (height) {
    params.append('h', height);
  }

  return `https://images.weserv.nl/?${params.toString()}`;
};

/**
 * Optimize Unsplash image URL
 * Unsplash has built-in optimization parameters
 *
 * @param {string} unsplashUrl - Unsplash image URL
 * @param {object} options - Options
 * @param {number} options.width - Target width
 * @param {number} options.height - Target height
 * @param {number} options.quality - Quality 1-100 (default: 85)
 * @returns {string} Optimized Unsplash URL
 */
export const optimizeUnsplash = (unsplashUrl, options = {}) => {
  const { width = 800, height = 600, quality = 85 } = options;

  try {
    const url = new URL(unsplashUrl);

    // Clear existing params and set optimized ones
    url.search = '';
    url.searchParams.set('w', width);
    url.searchParams.set('h', height);
    url.searchParams.set('auto', 'format'); // Auto WebP if supported
    url.searchParams.set('fit', 'crop');
    url.searchParams.set('q', quality);
    url.searchParams.set('dnt', '1'); // Do Not Track - prevents cookies

    return url.toString();
  } catch (error) {
    console.error('Error optimizing Unsplash URL:', error);
    return unsplashUrl;
  }
};

/**
 * Optimize Wikimedia/Wikipedia image URL
 *
 * @param {string} wikimediaUrl - Wikimedia Commons URL
 * @param {object} options - Optimization options
 * @returns {string} Optimized image URL via CDN
 */
export const optimizeWikimedia = (wikimediaUrl, options = {}) => {
  // Wikimedia images are massive - always use CDN optimization
  return optimizeImage(wikimediaUrl, {
    width: options.width || 1200,
    height: options.height || 800,
    quality: options.quality || 85,
    format: 'webp'
  });
};

/**
 * Generate responsive image srcSet for different screen sizes
 *
 * @param {string} imageUrl - Original image URL
 * @param {array} sizes - Array of widths [400, 800, 1200]
 * @param {string} format - Image format (default: 'webp')
 * @returns {string} srcSet string
 */
export const generateSrcSet = (imageUrl, sizes = [400, 800, 1200], format = 'webp') => {
  return sizes
    .map(width => {
      const optimizedUrl = optimizeImage(imageUrl, { width, format });
      return `${optimizedUrl} ${width}w`;
    })
    .join(', ');
};

/**
 * Generate picture element markup for maximum optimization
 *
 * @param {string} imageUrl - Original image URL
 * @param {object} options - Options
 * @param {string} options.alt - Alt text
 * @param {string} options.className - CSS class
 * @param {boolean} options.lazy - Enable lazy loading (default: true)
 * @param {number} options.width - Display width
 * @param {number} options.height - Display height
 * @returns {object} Props for React component
 */
export const getOptimizedImageProps = (imageUrl, options = {}) => {
  const {
    alt = '',
    className = '',
    lazy = true,
    width = 800,
    height = 600
  } = options;

  // Detect image source
  const isUnsplash = imageUrl.includes('unsplash.com');
  const isWikimedia = imageUrl.includes('wikimedia.org') || imageUrl.includes('wikipedia.org');

  let optimizedUrl;
  if (isUnsplash) {
    optimizedUrl = optimizeUnsplash(imageUrl, { width, height });
  } else if (isWikimedia) {
    optimizedUrl = optimizeWikimedia(imageUrl, { width, height });
  } else {
    optimizedUrl = optimizeImage(imageUrl, { width, height });
  }

  return {
    src: optimizedUrl,
    srcSet: generateSrcSet(imageUrl, [400, 800, 1200]),
    sizes: '(max-width: 640px) 400px, (max-width: 1024px) 800px, 1200px',
    alt,
    className,
    loading: lazy ? 'lazy' : 'eager',
    width,
    height,
    decoding: 'async'
  };
};

/**
 * React component helper for optimized images
 *
 * Usage:
 * import { OptimizedImage } from '@/utils/imageOptimizer';
 *
 * <OptimizedImage
 *   src="https://images.unsplash.com/photo-xxx"
 *   alt="Description"
 *   width={800}
 *   height={600}
 * />
 */
export const OptimizedImage = ({ src, alt, width = 800, height = 600, className = '', lazy = true }) => {
  const props = getOptimizedImageProps(src, { alt, width, height, className, lazy });

  // eslint-disable-next-line jsx-a11y/alt-text
  return <img {...props} />;
};

/**
 * React component for picture element with WebP support
 *
 * Usage:
 * import { OptimizedPicture } from '@/utils/imageOptimizer';
 *
 * <OptimizedPicture
 *   src="https://images.unsplash.com/photo-xxx"
 *   alt="Description"
 *   width={800}
 *   height={600}
 * />
 */
export const OptimizedPicture = ({ src, alt, width = 800, height = 600, className = '', lazy = true }) => {
  const isUnsplash = src.includes('unsplash.com');

  // Generate fallback URL (JPG)
  const jpgUrl = isUnsplash
    ? optimizeUnsplash(src, { width, height })
    : optimizeImage(src, { width, height, format: 'jpg' });

  return (
    <picture>
      <source
        srcSet={generateSrcSet(src, [400, 800, 1200], 'webp')}
        sizes="(max-width: 640px) 400px, (max-width: 1024px) 800px, 1200px"
        type="image/webp"
      />
      <img
        src={jpgUrl}
        srcSet={generateSrcSet(src, [400, 800, 1200], 'jpg')}
        sizes="(max-width: 640px) 400px, (max-width: 1024px) 800px, 1200px"
        alt={alt}
        className={className}
        loading={lazy ? 'lazy' : 'eager'}
        width={width}
        height={height}
        decoding="async"
      />
    </picture>
  );
};

const imageOptimizerUtils = {
  optimizeImage,
  optimizeUnsplash,
  optimizeWikimedia,
  generateSrcSet,
  getOptimizedImageProps,
  OptimizedImage,
  OptimizedPicture
};

export default imageOptimizerUtils;
